import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { GraduationCap, Lock, Check, LogOut, Loader2, ShieldCheck } from "lucide-react";

const perks = [
  "Full access to all courses (incl. SAP MM 6-week program)",
  "Every assessment, company mock & adaptive practice set",
  "Typing terminal, Voice & Accent, AR Call & AML simulators",
  "Progress tracking and performance analytics",
];

export default function Subscribe() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const { data: plan } = useQuery({
    queryKey: ["plan"],
    queryFn: async () => (await api.get("/plan")).data,
    enabled: !!user,
  });

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/auth", { replace: true });
    else if (user.role === "admin" || user.subscription_active) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user || user.role === "admin" || user.subscription_active)
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const price = plan?.price_inr ?? 4599;
  const duration = plan?.duration_label ?? "6 months";
  const expired = !!user.access_expires_at; // had access before, now lapsed

  const handleLogout = async () => { await logout(); navigate("/auth"); };

  return (
    <div className="min-h-screen bg-background text-foreground noise-overlay relative overflow-hidden flex flex-col" data-testid="subscribe-page">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] -z-0" />
      <header className="relative z-10 h-16 flex items-center justify-between px-6 lg:px-12 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">Elite Hire India</span>
        </div>
        <Button variant="ghost" onClick={handleLogout} className="gap-2 text-muted-foreground" data-testid="subscribe-logout">
          <LogOut className="w-4 h-4" /> Logout
        </Button>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-primary" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-2">
              {expired ? "Your access has expired" : "Activate your membership"}
            </h1>
            <p className="text-muted-foreground">
              Hi {user.name}, your account is created but access to the training modules requires an active membership.
            </p>
          </div>

          <div className="rounded-md border border-border bg-card overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-end justify-between mb-1">
                <span className="font-heading font-bold text-lg">Full Access</span>
                <div className="text-right">
                  <span className="font-mono text-3xl font-black">₹{price.toLocaleString("en-IN")}</span>
                  <span className="text-sm text-muted-foreground"> / {duration}</span>
                </div>
              </div>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Time-based · renews manually</p>
            </div>
            <div className="p-6 space-y-3">
              {perks.map((p) => (
                <div key={p} className="flex items-start gap-2.5 text-sm" data-testid="subscribe-perk">
                  <Check className="w-4 h-4 text-success shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{p}</span>
                </div>
              ))}
            </div>
            <div className="p-6 bg-secondary/40 border-t border-border">
              <div className="flex items-start gap-2.5 mb-3">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm mb-1">How to get access</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Pay <span className="font-semibold text-foreground">₹{price.toLocaleString("en-IN")}</span> to the administrator directly,
                    then share your registered email <span className="font-mono text-foreground">{user.email}</span>.
                    The admin will activate your {duration} access — refresh this page once confirmed.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full mt-4 rounded-md"
            onClick={() => window.location.reload()}
            data-testid="subscribe-refresh"
          >
            I've paid — refresh my access
          </Button>
        </div>
      </main>
    </div>
  );
}
